import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, Heading, Icon} from '@forecast-it/design-system';
import {FeatureFlagDetailData} from './featureFlagDetailData';
import {FeatureFlagAssignmentTable} from './FeatureFlagAssignmentTable';
import {FeatureFlagRolloutSummary} from './FeatureFlagRolloutSummary';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_LINK_TEXT,
	FEATURE_FLAG_NEUTRAL_FILL,
	featureFlagCardSurface,
} from './featureFlagSurface';
import {RolloutDirectory} from './rollout/featureFlagRolloutDirectory';
import {FeatureFlagRolloutModal} from './rollout/FeatureFlagRolloutModal';

const DetailWrapper = styled.div`
	max-width: 1400px;
	margin: 0 auto;
	padding: 20px 32px 64px;
`;

const BackLink = styled(Link)`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 14px;
	text-decoration: none;

	/* The typography components paint their own colour onto the element they render, so a colour
	   inherited from this link would lose to it and has to be set on the children instead. The arrow
	   takes its colour from its own prop and is unaffected. */
	> * {
		color: ${FEATURE_FLAG_LINK_TEXT};
	}

	&:hover {
		text-decoration: underline;
	}
`;

const HeaderCard = styled.div<{$accent: string}>`
	${featureFlagCardSurface}
	display: flex;
	flex-wrap: wrap;
	align-items: flex-start;
	justify-content: space-between;
	gap: 20px 32px;
	padding: 20px 24px;
	/* Repeats the accent bar on the row this page was opened from, so the two read as one place. */
	border-left: 4px solid ${props => props.$accent};
`;

const HeaderMain = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-width: 0;
`;

const HeaderTitleRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
`;

const TeamPill = styled.span`
	display: flex;
	flex: none;
	align-items: center;
	gap: 7px;
	white-space: nowrap;
	padding: 3px 9px;
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 999px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
`;

const TeamSwatch = styled.span<{$colour: string}>`
	width: 9px;
	height: 9px;
	border-radius: 2px;
	flex: none;
	background: ${props => props.$colour};
`;

export interface FeatureFlagDetailProps {
	detail: FeatureFlagDetailData;
	/** Where the flag list lives. Owned by the page, which holds the route. */
	backPath: string;
	/** Company names and per-company people the rollout modal picks from. */
	directory: RolloutDirectory;
	/** The pool the rollout percentage is measured against. */
	eligibleCompanyIds: Set<string>;
}

export function FeatureFlagDetail({detail, backPath, directory, eligibleCompanyIds}: FeatureFlagDetailProps): JSX.Element {
	const [isRolloutOpen, setIsRolloutOpen] = useState(false);
	// `null` is a real state distinct from closed: it is the modal open for adding, where no single
	// company is being edited. Hence the separate open flag rather than treating a company id as the
	// whole state.
	const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);

	const openRollout = (companyId: string | null): void => {
		setEditingCompanyId(companyId);
		setIsRolloutOpen(true);
	};

	return (
		<DetailWrapper>
			<BackLink data-cy="feature-flag-detail-back" to={backPath}>
				<Icon size="s" icon="arrowLeft" newColor="primary" />
				<Detail>All feature flags</Detail>
			</BackLink>
			<HeaderCard $accent={detail.teamColour}>
				<HeaderMain>
					<HeaderTitleRow>
						<Heading size="3">{detail.name}</Heading>
						<TeamPill>
							{/* Decorative: it restates the team named beside it, so an accessible name here
							    would make the pill announce its colour before its team. */}
							<TeamSwatch
								aria-hidden="true"
								data-testid="feature-flag-detail-team-swatch"
								$colour={detail.teamColour}
							/>
							<Detail>{detail.teamLabel}</Detail>
						</TeamPill>
					</HeaderTitleRow>
					<Detail color="medium">Flag name and owning team are defined in backend code — not editable here.</Detail>
				</HeaderMain>
				<Button emphasis={EMPHASIS.HIGH} icon="plus" onPress={() => openRollout(null)}>
					Roll out to more companies
				</Button>
			</HeaderCard>
			<FeatureFlagRolloutSummary detail={detail} />
			<FeatureFlagAssignmentTable onEditCompany={companyId => openRollout(companyId)} rows={detail.rows} />
			{isRolloutOpen ? (
				<FeatureFlagRolloutModal
					detail={detail}
					directory={directory}
					editingCompanyId={editingCompanyId}
					eligibleCompanyIds={eligibleCompanyIds}
					onClose={() => setIsRolloutOpen(false)}
				/>
			) : null}
		</DetailWrapper>
	);
}
